import { useTranslation } from 'react-i18next';
import { Label } from '../../Notifications/style/SingleNotificationStyle';
import { useCallback, useEffect, useState } from 'react';
import ServiceTypes, { types } from '../../../service/ServiceTypes';
import AttToken from '../../../helpers/attToken';
import Clients from '../../../service/Clients';
import { Autocomplete, Button, Skeleton, Stack, TextField } from '@mui/material';
import NovoChamado from '../../../service/NovoChamado';
import { toast } from 'react-toastify';
import queryClient from '../../../service/query';
import Users from '../../../service/Users';
import { userBackObj } from '../../Users/UserSearch';
import { trackEventMatomo } from '../../../helpers/matomo';
import GenericAcordonPlaces from '../../GenericAcordeonPlaces/GenericAcordeonPlaces';
import HeaderModal from '../../HeaderModal/HeaderModal';
import FooterModal from '../../FooterModal/FooterModal';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import QueryFn from '../../../helpers/QueryFn';

const queryFn = new QueryFn();

export type newTicketObjType = {
  id?: string,
  name: string,
  message: string,
  serviceType: string,
  daysLimit?: number | null,
  timeLimit?: number | null,
  setsList: string[],
  responsible: string,
  redirectable?: boolean
};

type props = {
  isEdit?: boolean,
  setIsEdit: Function,
  ticketEdit?: newTicketObjType | undefined,
  setTicketEdit: Function,
  openModal: boolean,
  setOpenModal: Function,
};

const serviceType = new ServiceTypes();
const CustomerService = new Clients();
const chamadoService = new NovoChamado();
const usersService = new Users();

export default function NewTicketModal({ isEdit, setIsEdit, ticketEdit, setTicketEdit, openModal, setOpenModal }: props) {
  const { t } = useTranslation('translation');
  const [cnjsArr, setCnjsArr] = useState(ticketEdit?.setsList || []);

  useEffect(() => {
    if (ticketEdit?.serviceType) {
      places.mutate(ticketEdit.serviceType);
    }
  }, []);

  const results = queryFn.useFetchMultiple([
    {
      key: 'GET_SERVICES',
      fn: async () => {
        try {
          const token = await AttToken();
          if (token) {
            const body = { ...token, empresa: token.userEmpresa };
            const result = await serviceType.getAllServices(body);
            return result;
          }
        } catch (err) {
          return err;
        }
      },
    },
    {
      key: 'GET_USERS',
      fn: async () => {
        try {
          const token = await AttToken();
          if (token) {
            const body = { ...token };
            const result = await usersService.getAllUsers(body);
            return result.users;
          }
        } catch (err) {
          return err;
        }
      },
    }
  ]);

  const isLoading = results?.some((result: any) => result.isLoading);
  const isFetching = results?.some((result: any) => result.isFetching);

  const places = queryFn.useMutate('GET_CLIENTES_CHILDREN', async (value: string) => {
    try {
      const token = await AttToken();
      if (token) {
        const response = await CustomerService.getAllWithSets(token, value);
        return response;
      }
    } catch (err) {
      console.error(err);
    }
  });

  const { mutate: createNewTicket } = queryFn.useMutate('newTicketModel',
    async () => {
      toast.warn(`${t('notifications.ticket.CriandoNovoModelo')}`);
      try {
        const token = await AttToken();
        if (token && ticketEdit) {
          const response = await chamadoService.createModel(token, ticketEdit);
          queryClient.resetQueries({ queryKey: ['GET_MODEL_LIST'] });
          setTicketEdit(undefined);
          setOpenModal(!openModal);
          toast.success(`${t('notifications.ticket.ModeloCriado')}`);
          return response;
        }
      } catch (err) {
        console.error(err);
        toast.error(`${t('notifications.ticket.ErroAoCriarModelo')}`);
      }
    });

  const { mutate: updateTicket } = queryFn.useMutate('updateTicketModel',
    async () => {
      toast.warn(`${t('notifications.ticket.AtualizandoModelo')}`);
      try {
        const token = await AttToken();
        if (token && ticketEdit) {
          const response = await chamadoService.updateModel(token, ticketEdit);
          queryClient.resetQueries({ queryKey: ['GET_MODEL_LIST'] });
          setTicketEdit(undefined);
          setOpenModal(!openModal);
          toast.success(`${t('notifications.ticket.ModeloAlterado')}`);
          return response;
        }
      } catch (err) {
        console.log(err);
        toast.error(`${t('notifications.ticket.ErroAoAlterar')}`);
      }
    });

  const close = useCallback(() => {
    trackEventMatomo('modal criação de chamado', 'click', 'button', 'fecha modal');
    setOpenModal(!openModal);
    setIsEdit(false);
  }, [openModal, setIsEdit, setOpenModal]);

  const submit = () => {
    trackEventMatomo('modal criação de chamado', 'click', 'button', 'salva');
    isEdit ? updateTicket('') :
      createNewTicket('');
  };

  useEffect(() => {
    setTicketEdit({ ...ticketEdit, setsList: cnjsArr });
  }, [cnjsArr]);

  useEffect(() => {
  }, [ticketEdit]);

  const escape = useCallback((e: any) => {
    if (e.key === 'Escape') {
      close();
    }
  }, [close]);

  useEffect(() => {
    document.addEventListener('keydown', escape, true);
  }, [escape]);

  return (
    <div className={`modal ${openModal ? "modal is-active" : "modal"}`}>
      <div className="modal-background" onClick={() => close()} onKeyDown={(e) => escape(e)} />
      <div className="modal-card">
        <HeaderModal
          close={close}
          headerTitle={!isEdit
            ? t('notifications.ticket.criarModelo')
            : t('notifications.ticket.editarModelo')} />
        <section className="modal-card-body" style={{ textAlign: 'initial' }}>
          <Stack
            direction="column"
            justifyContent="center"
            alignItems="stretch"
            spacing={1}
          >
            <TextField
              onClick={() => trackEventMatomo('modal criação de chamado', 'click', 'input', 'edita valor')}
              style={{ marginBottom: '10px' }}
              fullWidth
              size='small'
              value={ticketEdit?.name}
              label={t('name')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTicketEdit({ ...ticketEdit, name: e.target.value })}
            />
            <TextField
              size='small'
              onClick={() => trackEventMatomo('modal criação de chamado', 'click', 'input', 'edita valor')}
              style={{ marginBottom: '10px' }}
              fullWidth
              multiline
              // rows={2}
              maxRows={4}
              value={ticketEdit?.message}
              label={t('message')}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setTicketEdit({ ...ticketEdit, message: e.target.value })}
            />
            <div style={{ display: 'flex' }}>
              <Label>{t('notifications.ticket.redirectable')}</Label>&nbsp;
              <input
                onClick={() => trackEventMatomo('modal criação de chamado', 'click', 'input', 'edita valor')}
                type="checkbox"
                checked={ticketEdit?.redirectable}
                placeholder={t('notifications.ticket.redirectable')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTicketEdit({ ...ticketEdit, redirectable: e.target.checked })}
              />
            </div>
            <Stack
              direction="column"
              justifyContent="center"
              alignItems="stretch"
              spacing={1}
            >
              {isLoading || isFetching
                ? <Skeleton variant='rounded' width={'100%'} height={40} /> :
                <Autocomplete
                  size='small'
                  onClick={() => trackEventMatomo('modal criação de chamado', 'click', 'select', 'seleciona valor')}
                  value={ticketEdit?.responsible ? {
                    value: ticketEdit?.responsible as string,
                    label: (results[1]?.data as any[])?.find((ele: userBackObj) => ele.id === ticketEdit?.responsible)?.nome as string
                  } : undefined}
                  filterSelectedOptions
                  options={
                    (results[1].data as any[])
                      ?.sort((a: userBackObj, b: userBackObj) => { return a.nome.toLowerCase() > b.nome.toLowerCase() ? 1 : - 1; })
                      ?.filter((e: userBackObj) => e.status === 'ativo')
                      ?.filter((el: userBackObj) => el.aprovador === true)
                      ?.map((ele: any) => ({
                        label: ele.nome,
                        value: ele.id,
                      }))}
                  onChange={(event: any, newValue: { label: string, value: string } | null, reason: any, details: any) => {
                    if (newValue) {
                      setTicketEdit({ ...ticketEdit, responsible: newValue?.value });
                    } else setTicketEdit({ ...ticketEdit, responsible: null });
                  }}
                  renderOption={(props, option, { selected }) => (
                    <li {...props} key={option.value}>
                      <span>{option.label}</span>
                    </li>)}
                  renderInput={(params) => <TextField {...params} label={t('notifications.ticket.UsuárioResponsávelConclusao')} />}
                />
              }
              <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                spacing={1}
              >
                {isLoading || isFetching
                  ? <Skeleton variant='rounded' width={'75%'} height={40} /> :
                  <Autocomplete
                    size='small'
                    sx={{ width: '75%' }}
                    onClick={() => trackEventMatomo('modal criação de chamado', 'click', 'select', 'seleciona valor')}
                    id="combo-box-demo"
                    disabled={ticketEdit?.serviceType !== ''}
                    value={ticketEdit?.serviceType ? {
                      value: ticketEdit?.serviceType as string,
                      label: (results[0].data as any[])?.find((ele: types) => ele.tipoId === ticketEdit?.serviceType).nome
                    } : null}
                    filterSelectedOptions
                    options={
                      (results[0].data as any[])?.sort((a: types, b: types) => { return a.nome.toLowerCase() > b.nome.toLowerCase() ? 1 : - 1; })?.map((ele: any) => ({
                        label: ele.nome,
                        value: ele.tipoId,
                      }))}
                    onChange={(event: any, newValue: { label: string, value: string } | null, reason: any, details: any) => {
                      setTicketEdit({ ...ticketEdit, serviceType: details.option.value });
                      places.mutate(details.option.value);
                    }}
                    renderOption={(props, option) => (
                      <li {...props} key={option.value}>
                        <span>{option.label}</span>
                      </li>)}
                    renderInput={(params) => <TextField {...params} label={t('notifications.ticket.tipoServico')} />}
                  />
                }
                {ticketEdit?.serviceType !== "" &&
                  <Button
                    endIcon={<CompareArrowsIcon />}
                    onClick={() => {
                      trackEventMatomo('modal criação de chamado', 'click', 'button', 'desassocia tipo de serviço');
                      setTicketEdit({ ...ticketEdit, serviceType: "", setsList: [] });
                    }}
                    className='button is-danger'>
                    {t('formDetails.Association.desassociar')}
                  </Button>}
              </Stack>
            </Stack>
            {ticketEdit && ticketEdit?.serviceType?.length > 0
              && <GenericAcordonPlaces
                isLoading={places.isPending}
                conjuntosArray={cnjsArr}
                setConjuntosArray={setCnjsArr}
                clients={places.data}
              />
            }
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
            >
              <TextField
                size='small'
                onClick={() => trackEventMatomo('modal criação de chamado', 'click', 'input', 'edita valor')}
                fullWidth
                type='number'
                onChange={(event) => setTicketEdit({ ...ticketEdit, daysLimit: Number(event.target.value) })}
                value={ticketEdit?.daysLimit as number}
                label={t('ActionPlan.PlanosDeAcaoComp.days')}
              />
              <TextField
                size='small'
                onClick={() => trackEventMatomo('modal criação de chamado', 'click', 'input', 'edita valor')}
                fullWidth
                type='number'
                onChange={(event) => setTicketEdit({ ...ticketEdit, timeLimit: Number(event.target.value) })}
                value={ticketEdit?.timeLimit as number}
                label={t('hour')}
              />
            </Stack>
          </Stack>
        </section>
        <FooterModal
          label={`${t('save')}`}
          submit={submit}
          disabled={
            ticketEdit?.name === '' ||
            ticketEdit?.message === '' ||
            ticketEdit?.serviceType === '' ||
            !ticketEdit?.setsList?.length
          }
        />
      </div>
    </div >
  );
}
